import { useCallback, useState } from "react";
import {
  requestDaveyBleDevice,
  resolveBleDeviceName,
} from "utils/bleDeviceName";
import {
  readWifiSSID as readWifiSSIDValue,
  sendWifiCredentials as sendWifiCredentialsValue,
} from "utils/bleWifi";

interface AquapilotDeviceContext {
  scanForLifeguard: () => Promise<BluetoothDevice | null>;
  connectToDevice: (
    device: BluetoothDevice,
  ) => Promise<BluetoothRemoteGATTServer | void>;
  disconnectFromDevice: () => void;
  writeCharacteristic: (value: string) => Promise<void>;
  readCharacteristic: () => Promise<string | null>;
  sendWifiCredentials: (
    server: BluetoothRemoteGATTServer,
    serviceUUID: string,
    ssidCharacteristicUUID: string,
    passwordCharacteristicUUID: string,
    encMethodCharacteristicUUID: string,
    ssid: string,
    password: string,
  ) => Promise<void>;
  readWifiSSID: (
    server: BluetoothRemoteGATTServer,
    serviceUUID: string,
    ssidCharacteristicUUID: string,
  ) => Promise<string | null>;
  connectedDevice: BluetoothDevice | null;
  deviceDisplayName: string | null;
  isConnected: boolean;
  loading: boolean;
}

export const UUID_WIFI_SERVICE = "ff00c161-5d1a-4c5d-83f6-0671df21574d";
export const UUID_CHAR_WIFI_SSID = "0000ff02-0000-1000-8000-00805f9b34fb";
export const UUID_CHAR_WIFI_PASSWORD = "0000ff04-0000-1000-8000-00805f9b34fb";
export const UUID_CHAR_WIFI_ENC_METHOD = "0000ff03-0000-1000-8000-00805f9b34fb";

export function useBLEforAquapilotWeb(): AquapilotDeviceContext {
  const [connectedDevice, setConnectedDevice] =
    useState<BluetoothDevice | null>(null);
  const [deviceDisplayName, setDeviceDisplayName] = useState<string | null>(
    null,
  );
  const [server, setServer] = useState<BluetoothRemoteGATTServer | null>(null);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const scanForLifeguard = useCallback(async () => {
    try {
      setLoading(true);
      setDeviceDisplayName(null);

      const result = await requestDaveyBleDevice([UUID_WIFI_SERVICE]);
      if (!result) {
        console.error("No device found.");
        return null;
      }

      const { device, displayName } = result;
      setDeviceDisplayName(displayName || device.name || null);
      setConnectedDevice(device);

      device.addEventListener("gattserverdisconnected", () => {
        setIsConnected(false);
        setServer(null);
      });

      return device;
    } catch (error) {
      console.error("Error scanning for devices: ", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const connectToDevice = useCallback(
    async (device: BluetoothDevice) => {
      try {
        setLoading(true);

        if (isConnected && server) {
          console.log(
            "Device already connected:",
            deviceDisplayName || device.name,
          );
          return server;
        }

        let gattServer: BluetoothRemoteGATTServer | null = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            gattServer = await (device.gatt?.connect() ?? null);
            if (gattServer) break;
          } catch (err) {
            console.log(`Attempt ${attempt + 1} failed:`, err);
            if (attempt === 1) throw err;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        if (!gattServer) throw new Error("Failed to connect to GATT Server.");

        const resolvedName = await resolveBleDeviceName(device, 1500);
        if (resolvedName) setDeviceDisplayName(resolvedName);

        console.log("Connected to device:", resolvedName || device.name);
        setServer(gattServer);
        setIsConnected(true);
        return gattServer;
      } catch (error) {
        console.error("Error connecting to device:", error);
        setIsConnected(false);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [isConnected, server, deviceDisplayName],
  );

  const disconnectFromDevice = useCallback(() => {
    if (!server) return;
    server.disconnect();
    setServer(null);
    setConnectedDevice(null);
    setDeviceDisplayName(null);
    setIsConnected(false);
  }, [server]);

  const writeCharacteristic = useCallback(
    async (value: string) => {
      if (!server) return;
      try {
        setLoading(true);
        const service = await server.getPrimaryService(UUID_WIFI_SERVICE);
        const characteristic = await service.getCharacteristic(
          UUID_CHAR_WIFI_SSID,
        );
        await characteristic.writeValue(new TextEncoder().encode(value));
      } catch (error) {
        console.error("Error writing to characteristic:", error);
      } finally {
        setLoading(false);
      }
    },
    [server],
  );

  const readCharacteristic = useCallback(async (): Promise<string | null> => {
    if (!server) return null;
    try {
      setLoading(true);
      const service = await server.getPrimaryService(UUID_WIFI_SERVICE);
      const characteristic = await service.getCharacteristic(
        UUID_CHAR_WIFI_SSID,
      );
      const value = await characteristic.readValue();
      return new TextDecoder().decode(value);
    } catch (error) {
      console.error("Error reading characteristic:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [server]);

  const sendWifiCredentials = useCallback(
    async (
      gattServer: BluetoothRemoteGATTServer,
      serviceUUID: string,
      ssidCharacteristicUUID: string,
      passwordCharacteristicUUID: string,
      encMethodCharacteristicUUID: string,
      ssid: string,
      password: string,
    ) => {
      await sendWifiCredentialsValue(
        gattServer,
        serviceUUID,
        ssidCharacteristicUUID,
        passwordCharacteristicUUID,
        encMethodCharacteristicUUID,
        ssid,
        password,
      );
    },
    [],
  );

  const readWifiSSID = useCallback(
    async (
      gattServer: BluetoothRemoteGATTServer,
      serviceUUID: string,
      ssidCharacteristicUUID: string,
    ) => {
      setLoading(true);
      try {
        return await readWifiSSIDValue(
          gattServer,
          serviceUUID,
          ssidCharacteristicUUID,
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    scanForLifeguard,
    connectToDevice,
    sendWifiCredentials,
    disconnectFromDevice,
    readWifiSSID,
    writeCharacteristic,
    readCharacteristic,
    connectedDevice,
    deviceDisplayName,
    isConnected,
    loading,
  };
}
